/**
 * World profiles (GDD §3.3; concept L42–L114): river centerline and half-width, terrain height and colour.
 * 'valley' is v1; 'canyon' is kept for the v1.1 chase camera (GDD §2).
 */
import { Color } from 'three';
import { clamp, fract, lerp, sstep, wrapDz } from '../core/math.ts';
import { pd, pn, ZK } from './noise.ts';

export const ISLAND_Z = -420; // concept L8

export interface WorldProfile {
  /** River centerline x at z (m). */
  center(z: number): number;
  /** River half-width at z (m): centerline to bank (GDD §3.3). */
  width(z: number): number;
  height(x: number, z: number): number;
  /** Terrain colour for height h, slope, distance d outside the bank (negative inside the river). */
  color(out: Color, h: number, slope: number, d: number, x: number, z: number): void;
  island: boolean;
  shore: number;
  bridgeZ: number;
  deckY: number;
  barges: number[];
  boats: number[];
  helis: number[];
}

const col = (hex: number) => new Color(hex);

// Valley palette (GDD §3.3; concept L76).
const VP = {
  bed: col(0x4a4535),
  sand: col(0xc2a57c),
  wet: col(0x7c6a50),
  grass: col(0x648f40),
  grass2: col(0x41702f),
  forest: col(0x2c4b25),
  rock: col(0x7b7468),
  rock2: col(0x958d7e),
  high: col(0xa39b8b),
};
// Canyon palette (concept L88).
const CP = {
  bed: col(0x4a3b2c),
  sand: col(0xcaa878),
  s1: col(0x94401f),
  s2: col(0xc4662f),
  s3: col(0xe3a468),
  s4: col(0x5e2615),
  top: col(0xc49165),
  scrub: col(0x77733f),
};

function valleyHeight(x: number, z: number, W: WorldProfile): number {
  const c = W.center(z);
  const w = W.width(z);
  const d = Math.abs(x - c) - w;
  let h: number;
  if (d < 0) h = -0.35 - 4.5 * sstep(0, 14, -d);
  else {
    const n = pn(x, z);
    const n2 = pn(x * 0.6 + 40, z);
    h =
      -0.35 +
      1.6 * sstep(0, 5, d) +
      (3 + 2.5 * n) * sstep(4, 26, d) +
      26 * sstep(22, 120, d) * (0.55 + 0.45 * n2) +
      75 * sstep(140, 300, d) * (0.6 + 0.4 * pn(x * 0.35 + 90, z)) +
      0.6 * pd(x, z) * sstep(3, 12, d);
  }
  if (W.island) {
    const dz = wrapDz(z - ISLAND_Z);
    if (Math.abs(dz) < 80) {
      const rx = 0.36 * w;
      const rz = 44;
      const ex = (x - c) / rx;
      const ez = dz / rz;
      const e = ex * ex + ez * ez;
      if (e < 1.6) {
        const ih = e < 1 ? -0.6 + 6.8 * Math.pow(1 - e, 0.55) + 0.5 * pd(x, z) * (1 - e) : -0.6 - 4 * sstep(1, 1.6, e);
        if (ih > h) h = ih;
      }
    }
  }
  return h;
}

function canyonHeight(x: number, z: number, W: WorldProfile): number {
  const c = W.center(z);
  const w = W.width(z);
  const d = Math.abs(x - c) - w;
  if (d < 0) return -0.35 - 4.5 * sstep(0, 10, -d);
  const n = pn(x, z);
  const jag = 3.2 * pn(x * 1.9 + 11, z) + 1.4 * pd(x, z);
  const cliff = 64 * sstep(3, 15, d + jag);
  const t = cliff / 7.5;
  const terr = 7.5 * (Math.floor(t) + sstep(0.55, 1, fract(t)));
  let h = -0.35 + 1.3 * sstep(0, 3, d) + lerp(cliff, terr, 0.75);
  h += 7 * n * sstep(18, 40, d) + 40 * sstep(110, 260, d) * (0.5 + 0.5 * pn(x * 0.4 + 70, z));
  return h;
}

function valleyColor(out: Color, h: number, slope: number, d: number, x: number, z: number): void {
  if (h < -0.2) {
    out.copy(VP.bed);
    return;
  }
  const n = pn(x * 2.3 + 5, z) * 0.5 + 0.5;
  const f = pd(x, z) * 0.5 + 0.5;
  out.copy(VP.grass).lerp(VP.grass2, n);
  out.lerp(VP.forest, sstep(8, 30, d) * (0.35 + 0.5 * f));
  out.lerp(VP.sand, 1 - sstep(0.6, 2.2, h));
  out.lerp(VP.wet, 1 - sstep(-0.2, 0.35, h));
  const rk = clamp(sstep(0.55, 0.95, slope) * 0.9 + sstep(45, 70, h) * 0.8, 0, 1);
  out.lerp(f > 0.5 ? VP.rock2 : VP.rock, rk);
  out.lerp(VP.high, sstep(80, 115, h));
}

function canyonColor(out: Color, h: number, slope: number, _d: number, x: number, z: number): void {
  if (h < -0.2) {
    out.copy(CP.bed);
    return;
  }
  const sh = 1.2 * pn(x * 1.3, z);
  const b = Math.sin(h * 0.55 + sh) * 0.5 + 0.5;
  const b2 = Math.sin(h * 0.23 + 0.6 + sh * 0.5) * 0.5 + 0.5;
  const b3 = Math.sin(h * 2.6 + pd(x, z) * 2.0) * 0.5 + 0.5;
  out.copy(CP.s1).lerp(CP.s2, b);
  out.lerp(CP.s3, Math.pow(b2, 2) * 0.85);
  out.lerp(CP.s4, Math.pow(1 - b3, 3) * 0.45 + (1 - sstep(0, 18, h)) * 0.25);
  const flat = 1 - sstep(0.35, 0.7, slope);
  out.lerp(CP.top, flat * sstep(6, 20, h) * 0.85);
  out.lerp(CP.scrub, flat * sstep(10, 30, h) * (pd(x, z) * 0.5 + 0.5) * 0.35);
  out.lerp(CP.sand, 1 - sstep(0.5, 2.2, h));
}

const valley: WorldProfile = {
  center: (z) => 32 * Math.sin(z * ZK * 2 + 0.4) + 12 * Math.sin(z * ZK * 5 + 1.1),
  width: (z) => {
    const di = wrapDz(z - ISLAND_Z);
    return 34 + 8 * Math.sin(z * ZK * 3 + 2.0) + 5 * Math.sin(z * ZK * 7) + 20 * Math.exp(-(di * di) / 4900);
  },
  height: (x, z) => valleyHeight(x, z, valley),
  color: valleyColor,
  island: true,
  shore: 1.45,
  bridgeZ: -700,
  deckY: 11,
  barges: [-190, -610],
  boats: [-300, -770],
  helis: [-120, -540],
};

const canyon: WorldProfile = {
  center: (z) => 26 * Math.sin(z * ZK * 3 + 0.9) + 8 * Math.sin(z * ZK * 7 + 2.3),
  width: (z) => 29 + 6 * Math.sin(z * ZK * 4 + 1.2) + 3 * Math.sin(z * ZK * 11),
  height: (x, z) => canyonHeight(x, z, canyon),
  color: canyonColor,
  island: false,
  shore: 0.9,
  bridgeZ: -730,
  deckY: 12,
  barges: [-260, -640],
  boats: [-420],
  helis: [-160, -520],
};

export const WORLDS = { valley, canyon } as const;
